import { useState, useCallback, useRef, useEffect } from "react";
import { authService } from "../services/authService";
import { nextFamilyOnFailure } from "../lib/logger";
import type { ChatMessage, ChatToolUse, ChatToolResult } from "../types/chat";
import type { SessionState } from "../types/session";
import { runtimeConfig } from "../runtimeConfig";

const API_BASE = runtimeConfig.apiBaseUrl;

export function useChatAgent(
  sessionId: string,
  // Task 175: the live session state (from SessionView.session.state) so sendMessage can gate on a
  // ready VM, and a resume trigger (startSession + fetchSession — see SessionView.ensureSessionOn).
  // Passed as params, NOT re-fetched here (fiche requirement).
  sessionState: SessionState | null = null,
  ensureSessionOn: () => void = () => {},
) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<string | null>(null);
  const [iteration, setIteration] = useState<{ n: number; max: number } | null>(null);
  const [budgetExhausted, setBudgetExhausted] = useState(false);
  // Task 175: resume-in-progress flag (exposed for consumers; the visible indicator is driven by
  // phase === "resuming" → PHASE_LABELS in ChatPanel). Refs hold the message to re-send after the
  // VM wakes, a one-retry guard for the race safety-net, and the give-up deadline timer handle.
  const [resuming, setResuming] = useState(false);
  const pendingTextRef = useRef<string | null>(null);
  const resumeRetriedRef = useRef(false);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Task 158: the model is chosen entirely by the backend (155). The UI no longer selects
  // it — it displays whichever model the run reports via the `model` SSE event. `null` until
  // the first event of the first run (badge shows "—" meanwhile).
  const [activeModel, setActiveModel] = useState<string | null>(null);
  // Task 169: in-flight state for the "generate summary" action (POST can take ~10-20s).
  const [summaryLoading, setSummaryLoading] = useState(false);
  // Task 172: the generated summary file (169), kept as a download card instead of being
  // downloaded immediately. `null` until the user generates a summary in this session.
  const [summaryFile, setSummaryFile] = useState<{ filename: string; downloadUrl: string } | null>(null);
  const [agentMode, setAgentMode] = useState<"cdp" | "a11y" | "visual">("cdp");
  const abortRef = useRef<AbortController | null>(null);
  const a11yErrorCountRef = useRef(0);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Detect agent mode from app capabilities (has_cdp)
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = authService.getToken();
        const headers: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        const sessionRes = await fetch(`${API_BASE}/sessions/${sessionId}`, { headers });
        if (!sessionRes.ok || cancelled) return;
        const session = await sessionRes.json() as { app?: string };
        const appName = session.app ?? "firefox";
        const appRes = await fetch(`${API_BASE}/apps/${appName}`, { headers });
        if (!appRes.ok || cancelled) return;
        const app = await appRes.json() as { capabilities?: { has_cdp?: boolean; has_atspi?: boolean } };
        const hasCdp = app.capabilities?.has_cdp ?? true;
        const hasAtspi = app.capabilities?.has_atspi ?? false;
        if (!cancelled) setAgentMode(hasCdp ? "cdp" : hasAtspi ? "a11y" : "visual");
      } catch {
        // default cdp
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Load conversation history from backend on mount
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    (async () => {
      try {
        const token = authService.getToken();
        const res = await fetch(`${API_BASE}/sessions/${sessionId}/chat`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return; // silently ignore (session may not exist yet)
        const data = await res.json();
        if (!cancelled && Array.isArray(data.messages) && data.messages.length > 0) {
          setMessages(data.messages.map((m: ChatMessage) => ({
            id: m.id ?? crypto.randomUUID(),
            role: m.role,
            content: m.content,
            toolUses: m.toolUses ?? [],
            toolResults: m.toolResults ?? [],
            ts: m.ts ?? Date.now(),
          })));
        }
      } catch {
        // ignore fetch errors on load
      }
    })();

    return () => { cancelled = true; };
  }, [sessionId]);

  // Live mirror: render chat activity initiated by other clients (API, orchestrators)
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let controller: AbortController | null = null;

    (async () => {
      while (!cancelled) {
        try {
          const token = authService.getToken();
          controller = new AbortController();
          const res = await fetch(`${API_BASE}/sessions/${sessionId}/chat/stream`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          });
          if (!res.ok || !res.body) throw new Error(`watch stream ${res.status}`);

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let eventType = "";
          let watcherMsgId: string | null = null;
          let content = "";
          let toolUses: ChatToolUse[] = [];
          let toolResults: ChatToolResult[] = [];

          const ensureMsg = (): string => {
            if (!watcherMsgId) {
              watcherMsgId = crypto.randomUUID();
              content = "";
              toolUses = [];
              toolResults = [];
              const id = watcherMsgId;
              setMessages(prev => [...prev, {
                id, role: "assistant", content: "", toolUses: [], toolResults: [], ts: Date.now(),
              }]);
            }
            return watcherMsgId;
          };

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
              if (line.startsWith("event:")) {
                eventType = line.slice(line.indexOf(":") + 1).trim();
                continue;
              }
              if (!line.startsWith("data:") || !eventType) continue;
              const et = eventType;
              eventType = "";
              // Own POST already renders its events — skip mirrored duplicates
              if (isLoadingRef.current) continue;
              try {
                const data = JSON.parse(line.slice(line.indexOf(":") + 1).trim());
                if (et === "user_message") {
                  watcherMsgId = null;
                  setMessages(prev => [...prev, {
                    id: crypto.randomUUID(), role: "user", content: data.content, ts: Date.now(),
                  }]);
                } else if (et === "text") {
                  const id = ensureMsg();
                  content += data.content;
                  setMessages(prev => prev.map(m => m.id === id ? { ...m, content } : m));
                } else if (et === "tool_use") {
                  const id = ensureMsg();
                  toolUses = [...toolUses, { id: data.id, name: data.name, input: data.input }];
                  setMessages(prev => prev.map(m => m.id === id ? { ...m, toolUses: [...toolUses] } : m));
                } else if (et === "tool_result") {
                  const id = ensureMsg();
                  toolResults = [...toolResults, {
                    toolUseId: data.tool_use_id, content: data.content, isScreenshot: data.is_screenshot,
                  }];
                  setMessages(prev => prev.map(m => m.id === id ? { ...m, toolResults: [...toolResults] } : m));
                } else if (et === "phase") {
                  setPhase(data.name);
                } else if (et === "iteration") {
                  setIteration({ n: data.n, max: data.max });
                } else if (et === "done") {
                  setPhase(null);
                  setIteration(null);
                  watcherMsgId = null;
                }
              } catch {
                // skip malformed JSON
              }
            }
          }
        } catch {
          // connection lost or aborted — retry below unless unmounted
        }
        if (!cancelled) await new Promise(r => setTimeout(r, 3000));
      }
    })();

    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [sessionId]);

  // Task 175: begin a resume — stash the message to re-send, show the "Reprise…" state, ask
  // SessionView to thaw the VM (startSession + fetchSession), and arm a one-shot give-up deadline.
  // This is NOT a poll (it fetches nothing) — the existing SessionView state-poll flips
  // `sessionState`, which drives the resume effect below.
  const beginResume = useCallback((text: string) => {
    pendingTextRef.current = text;
    setResuming(true);
    setPhase("resuming");
    ensureSessionOn();
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      if (pendingTextRef.current !== null) {
        pendingTextRef.current = null;
        setResuming(false);
        setIsLoading(false);
        setPhase(null);
        setError("Impossible de reprendre la session — réessayer");
      }
    }, 45000);
  }, [ensureSessionOn]);

  // Task 175: the actual POST + SSE stream, extracted from sendMessage so it runs either immediately
  // (session ready) or after a resume. Body unchanged except the session_not_ready race safety-net
  // and the loading-state guard in `finally`.
  const doSend = useCallback(async (text: string) => {
    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const token = authService.getToken();
      const res = await fetch(`${API_BASE}/sessions/${sessionId}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, mode: agentMode }),
        signal: controller.signal,
      });

      if (!res.ok) {
        // Task 175 safety-net: the session fell to `off` between the ready-check and this POST
        // (race) → 409 session_not_ready. Trigger a resume + retry ONCE (resumeRetriedRef), instead
        // of surfacing a raw error. The resume effect re-sends when the VM is back.
        let code: string | undefined;
        try { code = (await res.clone().json())?.code; } catch { /* non-JSON body */ }
        if (code === "session_not_ready" && !resumeRetriedRef.current) {
          resumeRetriedRef.current = true;
          beginResume(text);
          return;
        }
        const errBody = await res.text();
        throw new Error(`Chat API error: ${res.status} ${errBody}`);
      }
      resumeRetriedRef.current = false;

      // Read SSE stream
      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let buffer = "";
      let assistantContent = "";
      let toolUses: ChatToolUse[] = [];
      let toolResults: ChatToolResult[] = [];
      // Task 170: accumulate narration deltas (like assistantContent for `text`).
      let narrationAcc = "";
      const assistantId = crypto.randomUUID();

      // Add placeholder assistant message
      setMessages(prev => [...prev, {
        id: assistantId,
        role: "assistant",
        content: "",
        toolUses: [],
        toolResults: [],
        ts: Date.now(),
      }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        let eventType = "";
        for (const line of lines) {
          if (line.startsWith("event:")) {
            eventType = line.slice(line.indexOf(":") + 1).trim();
          } else if (line.startsWith("data:") && eventType) {
            try {
              const raw = line.slice(line.indexOf(":") + 1).trim();
              const data = JSON.parse(raw);

              if (eventType === "text") {
                assistantContent += data.content;
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, content: assistantContent } : m
                ));
              } else if (eventType === "tool_use") {
                const tu: ChatToolUse = { id: data.id, name: data.name, input: data.input };
                toolUses = [...toolUses, tu];
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, toolUses: [...toolUses] } : m
                ));
              } else if (eventType === "tool_result") {
                const tr: ChatToolResult = {
                  toolUseId: data.tool_use_id,
                  content: data.content,
                  isScreenshot: data.is_screenshot,
                };
                toolResults = [...toolResults, tr];
                // 2-strike fallback: A11Y_UNAVAILABLE × 2 → downgrade a11y → visual
                if (typeof data.content === "string" && data.content.startsWith("A11Y_UNAVAILABLE")) {
                  a11yErrorCountRef.current += 1;
                  const next = nextFamilyOnFailure("a11y", a11yErrorCountRef.current);
                  if (next) {
                    setAgentMode(next);
                    a11yErrorCountRef.current = 0;
                  }
                } else {
                  a11yErrorCountRef.current = 0; // reset on success
                }
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, toolResults: [...toolResults] } : m
                ));
              } else if (eventType === "narration") {
                // Task 170: incremental narration deltas → collapsible reasoning group.
                narrationAcc += data.content;
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, narration: narrationAcc } : m
                ));
              } else if (eventType === "phase") {
                setPhase(data.name);
              } else if (eventType === "model") {
                // Task 158: backend-resolved executor model for this run → badge indicator.
                if (typeof data.model === "string") setActiveModel(data.model);
              } else if (eventType === "file") {
                // Task 174: a "donne moi un pdf" request generated a downloadable file. Feed it into
                // the SAME summaryFile state as 172/173 → it appears as a download card (dedup by
                // download_url already handled by downloadFiles). Same shape as POST /chat/summary.
                if (typeof data.filename === "string" && typeof data.download_url === "string") {
                  setSummaryFile({ filename: data.filename, downloadUrl: data.download_url });
                }
              } else if (eventType === "iteration") {
                setIteration({ n: data.n, max: data.max });
              } else if (eventType === "budget_exhausted") {
                setBudgetExhausted(true);
              } else if (eventType === "mode") {
                if (data.mode === "cdp" || data.mode === "a11y" || data.mode === "visual") {
                  setAgentMode(data.mode as "cdp" | "a11y" | "visual");
                }
              } else if (eventType === "error") {
                setError(data.message);
              } else if (eventType === "done") {
                setPhase(null);
                setIteration(null);
              }
            } catch {
              // Skip malformed JSON
            }
            eventType = "";
          }
        }
      }
    } catch (e) {
      if (e instanceof Error && e.name !== "AbortError") {
        setError(e.message);
      }
    } finally {
      // Task 175: keep the loading state up while a resume is pending (the effect re-sends) so the
      // "Reprise…" indicator stays visible; otherwise clear it as before.
      if (pendingTextRef.current === null) {
        setIsLoading(false);
      }
      abortRef.current = null;
    }
  }, [sessionId, agentMode, beginResume]);

  // Task 175: new sendMessage — gate on session readiness BEFORE any POST. Ready → doSend now;
  // not ready → stash + resume, and let the resume effect send once the VM is back.
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    // Add user message locally (backend also stores it)
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      ts: Date.now(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);
    setIteration(null);
    setBudgetExhausted(false);
    a11yErrorCountRef.current = 0; // reset 2-strike counter for new message
    resumeRetriedRef.current = false;

    const ready = sessionState === "on" || sessionState === "ongoing" || sessionState === "connecting";
    if (!ready) {
      beginResume(text);
      return;
    }
    await doSend(text);
  }, [isLoading, sessionState, beginResume, doSend]);

  // Task 175: resume effect — once the session becomes ready and a message is pending, clear the
  // give-up timer and re-send it. Reuses SessionView's EXISTING state-poll (which flips
  // `sessionState`); this effect only listens to that prop, it creates no new timer/poll.
  useEffect(() => {
    const ready = sessionState === "on" || sessionState === "ongoing" || sessionState === "connecting";
    if (ready && pendingTextRef.current !== null) {
      if (resumeTimeoutRef.current) { clearTimeout(resumeTimeoutRef.current); resumeTimeoutRef.current = null; }
      const text = pendingTextRef.current;
      pendingTextRef.current = null;
      setResuming(false);
      setPhase(null);
      void doSend(text);
    }
  }, [sessionState, doSend]);

  const clearMessages = useCallback(async () => {
    setMessages([]);
    setError(null);
    // Clear on backend too
    try {
      const token = authService.getToken();
      await fetch(`${API_BASE}/sessions/${sessionId}/chat`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch {
      // ignore
    }
  }, [sessionId]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const continueRun = useCallback(() => {
    setBudgetExhausted(false);
    sendMessage("continue");
  }, [sendMessage]);

  // Task 172: generic browser-download helper — fetch a token-authed URL (the chat-files route,
  // 139) as a blob and trigger the browser "save" dialog. Reused by every download card
  // ("Télécharger") and by "Tout télécharger". Extracted from the old downloadSummary tail so the
  // download mechanism (route + blob) is unchanged; only the trigger moved to the cards.
  const downloadFile = useCallback(async (filename: string, downloadUrl: string) => {
    const fileRes = await fetch(downloadUrl);
    if (!fileRes.ok) throw new Error(`download failed (${fileRes.status})`);
    const blob = await fileRes.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  // Task 169/172: POST /chat/summary → backend generates a PDF (or .md fallback), returns a
  // token-authed download_url. Task 172 DECOUPLES generation from download: instead of downloading
  // immediately, store the result so a card appears in the download list — the user then downloads
  // it from there via `downloadFile`. Errors are surfaced via `error` (discreet), never a crash.
  const generateSummary = useCallback(async () => {
    if (summaryLoading) return;
    setSummaryLoading(true);
    try {
      const token = authService.getToken();
      const res = await fetch(`${API_BASE}/sessions/${sessionId}/chat/summary`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`summary failed (${res.status})`);
      const data = await res.json() as { filename: string; format: string; download_url: string };
      setSummaryFile({ filename: data.filename, downloadUrl: data.download_url });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Summary generation failed");
    } finally {
      setSummaryLoading(false);
    }
  }, [sessionId, summaryLoading]);

  return { messages, isLoading, error, phase, iteration, budgetExhausted, continueRun, sendMessage, clearMessages, cancel, activeModel, agentMode, generateSummary, downloadFile, summaryFile, summaryLoading, resuming };
}
